import { Button, CircularProgress, Stack } from "@mui/material";
import { useMemo } from "react";
import toast from "react-hot-toast";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import TextButton from "@/components/common/buttons/textButton";
import { useConfirm } from "@/components/providers/confirmProvider";
import { useConfirmResourcesOverbooking } from "@/hooks/appointments/useConfirmResourcesOverbooking";
import { useCreateMultiProviderAppointment } from "@/hooks/appointments/useCreateMultiProviderAppointment";
import { useEditMultiProviderAppointmentRequest } from "@/hooks/appointments/useEditMultiProviderAppointmentRequest";
import { useRebookMultiProviderAppointment } from "@/hooks/appointments/useRebookMultiProviderAppointment";
import useAction from "@/hooks/misc/useAction";
import useErrorLogger from "@/utils/useErrorLogger";
import { useMultiProviderAppointmentDrawerContext } from "./multiProviderAppointmentDrawerContext";
import { useMultiProviderAppointmentBookingWithPaymentLink } from "./useMultiProviderAppointmentBookingWithPaymentLink";

type Props = {
  onCloseDrawer: () => void;
  onCreateAppointmentSuccess: (appointmentId: string) => void;
  isDateConfirmed: boolean;
};

export const MultiProviderAppointmentDrawerFormActions = ({
  onCloseDrawer,
  onCreateAppointmentSuccess,
  isDateConfirmed,
}: Props) => {
  const {
    client,
    appointmentServices,
    telehealthType,
    isTelehealthAppointmentDetailsValid,
    isRebooking,
    isEditing,
    configurationToUpdate,
    telehealthVideoLink,
    telehealthAdditionalDetails,
    initialAppointment,
    appointmentId,
  } = useMultiProviderAppointmentDrawerContext();

  const logError = useErrorLogger();

  const createAppointmentOptions = {
    configurationToUpdate,
    appointmentServices,
    client,
    telehealthType,
    telehealthVideoLink,
    telehealthAdditionalDetails,
  };

  const handleCreateAppointment = useCreateMultiProviderAppointment(
    createAppointmentOptions
  );

  const handleCreateRebookingAppointment = useRebookMultiProviderAppointment({
    ...createAppointmentOptions,
    appointmentId,
  });

  const handleEditAppointment = useEditMultiProviderAppointmentRequest();

  const { getConfirm } = useConfirm();

  const confirmResourceOverbooking = useConfirmResourcesOverbooking({
    appointmentServices,
  });

  const areSelectedServicesSameApptType = useMemo(() => {
    return (
      appointmentServices.length === 0 ||
      appointmentServices
        .map((service) => service.serviceMenuItem.appointmentType)
        .reduce((acc, type) => acc.add(type), new Set()).size === 1
    );
  }, [appointmentServices]);

  const handleCreateNewAppointmentClick = async () => {
    const toastId = toast.loading("Booking the appointment...");

    try {
      const { data } = await handleCreateAppointment();
      toast.success("The appointment has been booked successfully!", {
        id: toastId,
      });
      onCreateAppointmentSuccess(
        data.createMultiProviderAppointment.appointment.id
      );
    } catch (errors) {
      toast.error(
        "We couldn't book the appointment at this time. Please try again later.",
        { id: toastId }
      );
      logError(`Exception while creating an appointment: ${errors}`);
    }
  };

  const handleCreateRebookingAppointmentClick = async () => {
    const toastId = toast.loading("Rebooking next appointment...");

    try {
      const { data } = await handleCreateRebookingAppointment();

      toast.success("The appointment has been rebooked successfully!", {
        id: toastId,
      });

      onCreateAppointmentSuccess(
        data.createMultiProviderAppointment.appointment.id
      );
    } catch (errors) {
      toast.error(
        "We couldn't rebook an appointment at this time. Please try again later.",
        { id: toastId }
      );
    }
  };

  const handleEditAppointmentClick = async () => {
    const result = await handleEditAppointment({
      initialAppointment,
      formAppointmentServices: appointmentServices,
    });

    if (result !== null) {
      onCloseDrawer();
    }
  };

  const handleCreateAppointmentClick = isRebooking
    ? handleCreateRebookingAppointmentClick
    : isEditing
      ? handleEditAppointmentClick
      : handleCreateNewAppointmentClick;

  const { createBookingWithPaymentLink } =
    useMultiProviderAppointmentBookingWithPaymentLink({
      createAppointment: handleCreateAppointmentClick,
      closeDrawer: onCloseDrawer,
    });

  const { hasFeaturePermission } = useUser();

  const confirmCreateAppointmentOnFrontDesk = async () => {
    const ok = await getConfirm({
      title: `You are about to complete a booking`,
      description: `You are completing a booking for ${client?.firstName} ${client?.lastName} without generating the CC link. Are you sure you want to continue with this booking?`,
      discardButtonText: "No, go back",
      confirmButtonText: "Confirm booking",
      confirmButtonDisabled: isLoadingCompleteBooking,
    });

    if (!ok) return;
    await handleCreateAppointmentClick();
  };

  const hasCollectingCCLinkPermission = hasFeaturePermission(
    FeaturePermission.GET_CLIENT_CREDIT_CARD_LINK
  );

  const hasConfirmCreateAppointmentOnFrontDeskPermission = hasFeaturePermission(
    FeaturePermission.CONFIRM_CREATE_APPOINTMENT_ON_FD
  );

  const {
    handleAction: handleCopyCollectCCLink,
    isLoading: isLoadingCopyCollectCCLink,
  } = useAction(
    async () => await confirmResourceOverbooking(createBookingWithPaymentLink)
  );

  const {
    handleAction: handleCompleteBooking,
    isLoading: isLoadingCompleteBooking,
  } = useAction(
    async () =>
      await confirmResourceOverbooking(
        hasConfirmCreateAppointmentOnFrontDeskPermission
          ? confirmCreateAppointmentOnFrontDesk
          : handleCreateAppointmentClick
      )
  );

  const canSubmitForm =
    client &&
    appointmentServices.length &&
    isDateConfirmed &&
    !isLoadingCompleteBooking &&
    !isLoadingCopyCollectCCLink &&
    (!telehealthType || isTelehealthAppointmentDetailsValid) &&
    areSelectedServicesSameApptType;

  return (
    <Stack
      sx={{
        flex: 1,
      }}
    >
      {hasCollectingCCLinkPermission && (
        <TextButton
          disabled={!canSubmitForm}
          onClick={handleCopyCollectCCLink}
          startIcon={
            <CircularProgress
              size="20px"
              color="inherit"
              sx={{ opacity: isLoadingCopyCollectCCLink ? 1 : 0 }}
            />
          }
          sx={{
            mb: 2,
          }}
        >
          Copy collect CC link to clipboard
        </TextButton>
      )}
      <Stack
        direction="row"
        sx={{
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          disabled={isLoadingCompleteBooking || isLoadingCopyCollectCCLink}
          sx={{ flex: 1 }}
          size="small"
          onClick={onCloseDrawer}
        >
          Cancel
        </Button>
        <Button
          disabled={!canSubmitForm}
          variant="contained"
          sx={{ flex: 1 }}
          size="small"
          onClick={handleCompleteBooking}
        >
          {isLoadingCompleteBooking ? (
            <CircularProgress size="20px" color="inherit" />
          ) : (
            <>
              {isEditing
                ? "Save changes"
                : hasCollectingCCLinkPermission
                  ? "Book without cc link"
                  : "Complete booking"}
            </>
          )}
        </Button>
      </Stack>
    </Stack>
  );
};
